import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import { adminApi } from '../../redux/adminApiSlice';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Place } from '../../types/interfaces';

interface Props {
  open?: boolean;
  onClose?: () => void;
  selectedPlaces: Place[];
  onSelectionChange: (places: Place[]) => void;
  onPlaceTypeSelect?: (type: string) => void;
  disabled?: boolean;
}

const STORAGE_KEY = 'mockPostPlaces';
const FAVORITES_KEY = 'favoritePlaces';

const PlaceSelector: React.FC<Props> = ({
  open,
  onClose,
  selectedPlaces,
  onSelectionChange,
  onPlaceTypeSelect,
  disabled
}) => {
  const { data: places, isLoading, refetch: refetchPlaces } = adminApi.useGetPlacesQuery();
  const { data: placeTypesData, isLoading: isLoadingTypes } = adminApi.useGetPlaceTypesQuery();
  const placeTypes = Array.isArray(placeTypesData)
    ? placeTypesData
    : placeTypesData && typeof placeTypesData === 'object'
      ? Array.from(new Set(Object.values(placeTypesData).flat().filter((t): t is string => typeof t === 'string')))
      : [];
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [favorites, setFavorites] = useState<Place[]>([]);
  const [selectedType, setSelectedType] = useState<string>('');

  // Load persisted selections and favorites on mount
  useEffect(() => {
    const savedSelections = localStorage.getItem(STORAGE_KEY);
    if (savedSelections && selectedPlaces.length === 0) {
      try {
        const parsed = JSON.parse(savedSelections);
        onSelectionChange(parsed);
      } catch (err) {
        console.error('Error parsing saved places:', err);
      }
    }

    const savedFavorites = localStorage.getItem(FAVORITES_KEY);
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (err) {
        console.error('Error parsing saved favorites:', err);
      }
    }
  }, [onSelectionChange, selectedPlaces.length]);

  // Save selections when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedPlaces));
  }, [selectedPlaces]);

  const ensurePlaceLevel = (place: Place): Place & { level: string } => ({ ...place, level: 'Place' });

  const handleToggle = (place: Place) => () => {
    const currentIndex = selectedPlaces.findIndex(p => p._id === place._id);
    const newSelected = [...selectedPlaces];

    if (currentIndex === -1) {
      newSelected.push(ensurePlaceLevel(place));
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onSelectionChange(newSelected.map(ensurePlaceLevel));
  };

  const handleRemoveSelected = (placeToRemove: Place) => {
    onSelectionChange(selectedPlaces.filter(p => p._id !== placeToRemove._id).map(ensurePlaceLevel));
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const toggleFavorite = (place: Place) => {
    const newFavorites = favorites.some(f => f._id === place._id)
      ? favorites.filter(f => f._id !== place._id)
      : [...favorites, place];
    
    setFavorites(newFavorites);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
  };

  const isFavorite = (place: Place) => 
    favorites.some(f => f._id === place._id);

  // Helper to get neighborhood name
  const getNeighborhoodName = (neighborhood: Place['neighborhood']) => {
    if (!neighborhood) return '';
    if (typeof neighborhood === 'string') return neighborhood;
    return neighborhood.name || '';
  };

  // Get the places to display based on current tab and search
  const getDisplayPlaces = () => {
    if (!places || !Array.isArray(places)) {
      return [];
    }
    
    const sourceList = currentTab === 0 ? places : favorites;
    
    if (!searchTerm) {
      return sourceList;
    }
    
    return sourceList.filter(p => 
      (p.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (getNeighborhoodName(p.neighborhood).toLowerCase()).includes(searchTerm.toLowerCase())
    );
  };

  const displayPlaces = getDisplayPlaces();
  const hasPlaces = places && places.length > 0;

  return (
    <Dialog 
      open={open ?? false} 
      onClose={onClose ?? (() => {})} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Select Places</Typography>
          <Button 
            size="small" 
            color="primary" 
            variant="outlined" 
            onClick={() => refetchPlaces()}
            startIcon={<RefreshIcon />}
          >
            Refresh
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left side - Selection list */}
          <Box sx={{ 
            width: '60%', 
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by place or neighborhood name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Box>

            <Tabs 
              value={currentTab} 
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{ px: 2 }}
            >
              <Tab label="All" />
              <Tab label="Favorites" />
              <Tab label="By Type" />
            </Tabs>

            {currentTab === 2 && (
              <Box sx={{ p: 2 }}>
                {isLoadingTypes ? (
                  <CircularProgress size={20} />
                ) : (
                  <TextField
                    select
                    label="Select Place Type"
                    value={selectedType}
                    onChange={e => {
                      setSelectedType(e.target.value);
                      if (onPlaceTypeSelect) onPlaceTypeSelect(e.target.value);
                    }}
                    fullWidth
                    SelectProps={{ native: true }}
                  >
                    <option value="">-- Select a type --</option>
                    {placeTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </TextField>
                )}
              </Box>
            )}

            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              px: 2
            }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !hasPlaces ? (
                <Alert severity="info">No places found. You may need to create some places first.</Alert>
              ) : (
                <List dense>
                  {displayPlaces.map((place) => {
                    const isSelected = selectedPlaces.some(p => p._id === place._id);
                    return (
                      <ListItem 
                        key={place._id} 
                        dense 
                        button 
                        onClick={handleToggle(place)}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="toggle favorite" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(place);
                            }}
                          >
                            {isFavorite(place) ? <StarIcon color="warning" /> : <StarBorderIcon />}
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={place.name}
                          secondary={getNeighborhoodName(place.neighborhood)}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>

          {/* Right side - Selected items */}
          <Box sx={{ 
            width: '40%',
            display: 'flex',
            flexDirection: 'column',
            p: 2
          }}>
            <Typography variant="subtitle1" gutterBottom>
              Selected Places ({selectedPlaces.length})
            </Typography>
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              bgcolor: 'background.default',
              borderRadius: 1,
              p: 1
            }}>
              {selectedPlaces.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ mt: 2 }}>
                  No places selected
                </Typography>
              ) : (
                <List dense>
                  {selectedPlaces.map((place) => (
                    <ListItem
                      key={place._id}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="remove"
                          onClick={() => handleRemoveSelected(place)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText 
                        primary={place.name}
                        secondary={getNeighborhoodName(place.neighborhood)}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClearAll} color="error">
          Clear All
        </Button>
        <Button onClick={onClose} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlaceSelector;